<template>
  <div class="flex flex-col">
    <div class="text-left flex flex-col gap-4">
      <Errors />
      <DynamicInputComponent
        v-for="field in fields"
        :key="field.key"
        :modelValue="form[field.key]"
        @update:modelValue="onInput"
        :field="field"
        :form="form"
        class="!p-0 !m-0"
        :debounce="false"
        :disabled="!props.canUpdate"
      />
      <primary-button
        class="flex"
        :label="t('components.buttons.update')"
        :loading="isUpdating"
        :disabled="!props.canUpdate"
        @click="update"
      />
    </div>
  </div>
</template>

<script setup>
// Import Vue Utilities
import { computed, ref } from "vue";

// Import Utilities
import { useI18n } from "vue-i18n";
import { usePage } from "@inertiajs/vue3";
import route from "ziggy-js";

// Import Components
import DynamicInputComponent from "@/Tailwind/Components/Settings/DynamicInputComponent.vue";
import PrimaryButton from "@/Tailwind/Components/Buttons/PrimaryButton.vue";
import Errors from "@/Tailwind/Components/Errors.vue";

// Import Mixins
import { useTenantCategories } from "@/Tailwind/Mixins/useCategories";
import { useFormSetup } from "@/Tailwind/Mixins/useFormSetup";

// Translations
const { t } = useI18n();

// Refs
const isUpdating = ref(false);

// Emits
const emit = defineEmits(["update:details"]);

// Define Props
const props = defineProps({
  space: Object,
  canUpdate: Boolean,
});

// Computed Props
const fields = computed(() => {
  if (props.space.type === "asset") {
    return [
      {
        key: "name",
        title: t("admin.settings.assets.name.title"),
        description: t("admin.settings.assets.name.description"),
        placeholder: t("admin.settings.assets.name.placeholder"),
        type: "text",
      },
      {
        key: "identifier",
        title: t("admin.settings.assets.identifier.title"),
        description: t("admin.settings.assets.identifier.description"),
        placeholder: t("admin.settings.assets.identifier.placeholder"),
        type: "text",
      },
      {
        key: "space",
        title: t("admin.settings.assets.space.title"),
        description: t("admin.settings.assets.space.description"),
        type: "space",
        extraAttributes: {
          tenantId: props.space.tenant_id,
          selectorStyle: "dropdown",
          hint: t("admin.settings.assets.space.description"),
        },
      },
      {
        key: "serial_number",
        title: t("admin.settings.assets.serial_number.title"),
        description: t("admin.settings.assets.serial_number.description"),
        placeholder: t("admin.settings.assets.serial_number.placeholder"),
        type: "text",
      },
      {
        key: "group",
        title: t("admin.settings.assets.asset_group.title"),
        type: "hierarchy",
        options: usePage().props.assetGroupsTree,
        extraAttributes: {
          hint: t("admin.settings.assets.asset_group.description"),
        },
      },
      {
        key: "brand",
        title: t("admin.settings.assets.brand.title"),
        description: t("admin.settings.assets.brand.description"),
        placeholder: t("admin.settings.assets.brand.placeholder"),
        type: "text",
      },
      {
        key: "supplier",
        title: t("admin.settings.assets.supplier.title"),
        description: t("admin.settings.assets.supplier.description"),
        placeholder: t("admin.settings.assets.supplier.placeholder"),
        type: "text",
      },
      {
        key: "category",
        title: t("admin.settings.assets.category.title"),
        type: "dropdown",
        options: useTenantCategories(props.space.tenant_id),
        extraAttributes: {
          hint: t("admin.settings.assets.category.description"),
          tenantId: props.space.tenant_id,
        },
      },
      {
        key: "purchased_at",
        title: t("admin.settings.assets.purchased_at.title"),
        type: "date",
        extraAttributes: {
          hint: t("admin.settings.assets.purchased_at.description"),
        },
      },
      {
        key: "comment",
        title: t("admin.settings.assets.comment.title"),
        type: "textarea",
        extraAttributes: {
          hint: t("admin.settings.assets.comment.description"),
        },
      },
    ];
  }

  if (props.space.type === "asset_group") {
    return [
      {
        key: "name",
        title: t("admin.settings.asset_group.name.title"),
        description: t("admin.settings.asset_group.name.description"),
        placeholder: t("admin.settings.asset_group.name.placeholder"),
        type: "text",
      },
      {
        key: "parent_group",
        title: t("admin.settings.asset_group.parent_group.title"),
        description: t("admin.settings.asset_group.parent_group.description"),
        type: "hierarchy",
        options: usePage().props.assetGroupsTree,
      },
    ];
  }

  return [
    {
      key: "name",
      title: t("admin.settings.location.name.title"),
      description: t("admin.settings.location.name.description"),
      placeholder: t("admin.settings.location.name.placeholder"),
      type: "text",
    },
    {
      key: "identifier",
      title: t("admin.settings.location.identifier.title"),
      description: t("admin.settings.location.identifier.description"),
      placeholder: t("admin.settings.location.identifier.placeholder"),
      type: "text",
    },
    {
      key: "type",
      title: t("admin.settings.location.type.title"),
      description: t("admin.settings.location.type.description"),
      placeholder: t("admin.settings.location.type.placeholder"),
      type: "dropdown",
      options: usePage().props.space_types.map((type) => ({
        value: type.value,
        label: t(`components.spaces.types.${type.value}.label`),
      })),
      default: "room",
      extraAttributes: {
        selectorStyle: "input",
      },
    },
    {
      key: "parent_id",
      title: t("admin.settings.location.parent_space.title"),
      description: t("admin.settings.location.parent_space.description"),
      placeholder: t("admin.settings.location.parent_space.placeholder"),
      type: "space",
      extraAttributes: {
        tenantId: props.space.tenant_id,
        selectorStyle: "dropdown",
      },
    },
  ];
});

const parsedSpaceDetails = computed(() => {
  if (!props.space) {
    return null;
  }

  if (props.space.type === "asset") {
    return {
      ...props.space,
      category: props.space.category?.id,
    };
  }

  return props.space;
});

// Form
const { mapDetailsToForm } = useFormSetup();
let form = mapDetailsToForm(fields.value, parsedSpaceDetails.value);

// Functions
const onInput = (field, value) => {
  form[field.key] = value;
};

const update = () => {
  if (props.space.type === "asset") {
    form
      .transform((data) => {
        return {
          ...data,
          space_id: data.space?.id,
          group_id: data.group?.id || data.group,
          category_id: data.category?.id || data.category,
        };
      })
      .put(
        route("settings.assets.update", {
          tenant: props.space.tenant_id,
          asset: props.space.id,
          redirectBack: true,
        }),
        {
          onStart: () => (isUpdating.value = true),
          onFinish: () => {
            isUpdating.value = false;
            emit("update:details", form);
          },
        }
      );

    return;
  }

  if (props.space.type === "asset_group") {
    form
      .transform((data) => {
        return {
          ...data,
          parent_group_id: data.parent_group,
        };
      })
      .put(
        route("settings.assets.group.update", {
          tenant: props.space.tenant_id,
          group: props.space.id,
          redirectBack: true,
        }),
        {
          onStart: () => (isUpdating.value = true),
          onFinish: () => {
            isUpdating.value = false;
            emit("update:details", form);
          },
        }
      );

    return;
  }

  form
    .transform((data) => {
      return {
        ...data,
        parent_id: data.parent_id?.id ?? data.parent_id,
      };
    })
    .put(
      route("settings.spaces.update", {
        tenant: props.space.tenant_id,
        space: props.space.id,
        redirectBack: true,
      }),
      {
        onStart: () => (isUpdating.value = true),
        onFinish: () => {
          isUpdating.value = false;
          emit("update:details", form);
        },
      }
    );
};
</script>
